import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CitBulkAttachImportModal } from '../../components/forms/CitBulkAttachImportModal';
import { CitScListRow } from '../../components/forms/CitScListRow';
import { CitStList } from '../../components/forms/CitStList';
import {
  CitSummaryDetailModal,
  type CitSummaryModalKind,
} from '../../components/forms/CitSummaryDetailModal';
import { PageHeader } from '../../components/ui/PageHeader';
import {
  buildCitStListLines,
  citScSearchBlob,
  citStLineSearchBlob,
  citSubmissionListYear,
  citYearFromSalesConfirmationNo,
  compareCitSalesConfirmationNoDesc,
  groupCitStLinesByMonth,
  listCitStatusDetails,
  listCitUnpaidDepositDetails,
} from '../../lib/citList';
import { citMissingScAttachmentRows } from '../../lib/citBulkAttachImport';
import { fetchCitSubmissions } from '../../lib/citSubmissions';
import { runCitSocSyncFromPowerBi } from '../../lib/citSocSync';
import { usePortal } from '../../contexts/PortalContext';
import { useFormsSubmissionsRealtime } from '../../hooks/useFormsSubmissionsRealtime';
import type {
  ApplicationSubmission,
  CustomiseItemsTransferFormData,
} from '../../types/applicationForm';

type CitListView = 'sales-confirmation' | 'stock-transfer';

function currentCitListYear(): number {
  try {
    const parts = new Intl.DateTimeFormat('en-SG', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
    }).formatToParts(new Date());
    const year = Number(parts.find((p) => p.type === 'year')?.value);
    if (Number.isFinite(year)) return year;
  } catch {
    /* fall through */
  }
  return new Date().getFullYear();
}

export function CustomiseItemsTransferListPage() {
  const { basePath } = usePortal();
  const [rows, setRows] = useState<ApplicationSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [view, setView] = useState<CitListView>('sales-confirmation');
  const [summaryKind, setSummaryKind] = useState<CitSummaryModalKind | null>(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [missingScOnly, setMissingScOnly] = useState(false);
  /** Empty string = All years. */
  const [yearFilter, setYearFilter] = useState<string>(() => String(currentCitListYear()));

  const listBase = basePath ? `${basePath}/customise-items-transfer` : '/customise-items-transfer';
  const newPath = `${listBase}/new`;
  const homePath = basePath || '/forms';

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) setLoading(true);
    setError(null);
    try {
      setRows(await fetchCitSubmissions());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load submissions.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSubmissionsRealtime({
    tables: ['cit_submissions'],
    onChange: () => {
      void load({ silent: true });
    },
  });

  const yearOptions = useMemo(() => {
    const set = new Set<number>();
    set.add(currentCitListYear());
    for (const row of rows) {
      const year = citSubmissionListYear(row);
      if (year != null) set.add(year);
    }
    return [...set].sort((a, b) => b - a);
  }, [rows]);

  const yearScopedRows = useMemo(() => {
    if (!yearFilter) return rows;
    const year = Number(yearFilter);
    if (!Number.isFinite(year)) return rows;
    return rows.filter((row) => citSubmissionListYear(row) === year);
  }, [rows, yearFilter]);

  const missingScRows = useMemo(
    () => citMissingScAttachmentRows(yearScopedRows),
    [yearScopedRows],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = missingScOnly ? missingScRows : yearScopedRows;
    if (q) {
      const tokens = q.split(/\s+/).filter(Boolean);
      list = list.filter((row) => {
        const blob = citScSearchBlob(row);
        return tokens.every((token) => blob.includes(token));
      });
    }
    return [...list].sort((a, b) => {
      const aSc = String(
        (a.form_data as CustomiseItemsTransferFormData)?.sales_confirmation_no ?? '',
      ).trim();
      const bSc = String(
        (b.form_data as CustomiseItemsTransferFormData)?.sales_confirmation_no ?? '',
      ).trim();
      const bySc = compareCitSalesConfirmationNoDesc(aSc, bSc);
      if (bySc !== 0) return bySc;
      return b.submitted_at.localeCompare(a.submitted_at);
    });
  }, [yearScopedRows, search, missingScOnly, missingScRows]);

  const stMonthGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    let lines = buildCitStListLines(yearScopedRows);
    if (yearFilter) {
      const year = Number(yearFilter);
      if (Number.isFinite(year)) {
        lines = lines.filter((line) => {
          const fromSc = citYearFromSalesConfirmationNo(line.salesConfirmationNo);
          if (fromSc != null) return fromSc === year;
          return line.monthKey.startsWith(`${year}-`);
        });
      }
    }
    if (q) {
      const tokens = q.split(/\s+/).filter(Boolean);
      lines = lines.filter((line) => {
        const blob = citStLineSearchBlob(line);
        return tokens.every((token) => blob.includes(token));
      });
    }
    return groupCitStLinesByMonth(lines);
  }, [yearScopedRows, search, yearFilter]);

  const stLineCount = useMemo(
    () => stMonthGroups.reduce((sum, group) => sum + group.lines.length, 0),
    [stMonthGroups],
  );

  const stTotalCount = useMemo(
    () => buildCitStListLines(yearScopedRows).length,
    [yearScopedRows],
  );

  const unpaidDepositDetails = useMemo(
    () => listCitUnpaidDepositDetails(yearScopedRows),
    [yearScopedRows],
  );

  const stats = useMemo(() => {
    let neu = 0;
    let processing = 0;
    let completed = 0;
    let cancelled = 0;
    for (const row of yearScopedRows) {
      const data = row.form_data as CustomiseItemsTransferFormData;
      const status = data?.status;
      if (status === 'New') neu += 1;
      else if (status === 'Processing') processing += 1;
      else if (status === 'Completed') completed += 1;
      else if (status === 'Cancelled') cancelled += 1;
    }
    return {
      total: yearScopedRows.length,
      neu,
      processing,
      completed,
      cancelled,
      unpaid: unpaidDepositDetails.length,
    };
  }, [yearScopedRows, unpaidDepositDetails]);

  const statusModalRows = useMemo(() => {
    if (!summaryKind || summaryKind === 'unpaid') return [];
    return listCitStatusDetails(yearScopedRows, summaryKind);
  }, [yearScopedRows, summaryKind]);

  const detailPath = (id: string) => `${listBase}/${id}`;

  const stEntryCount = useMemo(
    () =>
      stMonthGroups.reduce(
        (sum, group) =>
          sum +
          new Set(group.lines.map((line) => `${line.submissionId}:${line.stockTransferNo}`)).size,
        0,
      ),
    [stMonthGroups],
  );

  const countLabel = loading
    ? 'Loading…'
    : view === 'stock-transfer'
      ? search.trim()
        ? `${stEntryCount} stock transfer${stEntryCount === 1 ? '' : 's'} · ${stLineCount} of ${stTotalCount} item${stTotalCount === 1 ? '' : 's'}`
        : `${stEntryCount} stock transfer${stEntryCount === 1 ? '' : 's'} · ${stLineCount} item${stLineCount === 1 ? '' : 's'}`
      : missingScOnly
        ? `${filteredRows.length} without SC file · ${yearScopedRows.length} in ${yearFilter || 'all years'}`
        : search.trim() || yearFilter
          ? `${filteredRows.length} of ${yearScopedRows.length} record${yearScopedRows.length === 1 ? '' : 's'}`
          : `${filteredRows.length} record${filteredRows.length === 1 ? '' : 's'}`;

  const searchPlaceholder =
    view === 'stock-transfer'
      ? 'Search by stock transfer no., item, customer, confirmation or status'
      : 'Search by confirmation no., item, customer, deposit or status';

  const handleSyncFromPowerBi = async () => {
    setSyncBusy(true);
    setSyncMessage(null);
    setError(null);
    try {
      const result = await runCitSocSyncFromPowerBi();
      setSyncMessage(
        `Synced ${result.soc_docs ?? 0} SOC · +${result.inserted ?? 0} new · ${result.updated ?? 0} updated · ${result.skipped_locked ?? 0} locked`,
      );
      await load({ silent: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'PowerBI sync failed.');
    } finally {
      setSyncBusy(false);
    }
  };

  return (
    <div className="cit-page">
      <PageHeader
        title="Sales Confirmation"
        description="2026+ SOC quotes sync from PowerBI. Upload the SC file to lock the row and choose deposit Yes/No."
        actions={
          <div className="cit-page__header-actions">
            <Link to={homePath} className="btn-secondary">
              All forms
            </Link>
            <button
              type="button"
              className="btn-primary"
              disabled={syncBusy}
              onClick={() => void handleSyncFromPowerBi()}
              title="Import / refresh 2026+ yyyy/SOC/mm/xxx from PowerBI (skips rows with SC attachment)"
            >
              {syncBusy ? 'Syncing…' : 'Sync from PowerBI'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setBulkOpen(true)}
              title="Temporary: fill SC PDF, deposit arrangement and status for rows without an SC file"
            >
              Bulk fill
              {missingScRows.length ? ` (${missingScRows.length})` : ''}
            </button>
            <Link to={newPath} className="btn-secondary" title="Rare manual entry (non-SOC / exceptions)">
              Manual entry
            </Link>
          </div>
        }
      />

      {syncMessage ? <p className="field-hint cit-page__sync-msg">{syncMessage}</p> : null}

      <div className="kpi-grid dashboard-kpis" role="toolbar" aria-label="Summary details">
        <button
          type="button"
          className="card kpi-card kpi-card--clickable"
          onClick={() => setSummaryKind('all')}
        >
          <div className="kpi-label">Total</div>
          <div className="kpi-value">{stats.total}</div>
        </button>
        <button
          type="button"
          className="card kpi-card kpi-card--clickable"
          onClick={() => setSummaryKind('New')}
        >
          <div className="kpi-label">New</div>
          <div className="kpi-value">{stats.neu}</div>
        </button>
        <button
          type="button"
          className="card kpi-card kpi-card--clickable"
          onClick={() => setSummaryKind('Processing')}
        >
          <div className="kpi-label">Processing</div>
          <div className="kpi-value">{stats.processing}</div>
        </button>
        <button
          type="button"
          className="card kpi-card kpi-card--clickable"
          onClick={() => setSummaryKind('Completed')}
        >
          <div className="kpi-label">Completed</div>
          <div className="kpi-value">{stats.completed}</div>
        </button>
        <button
          type="button"
          className="card kpi-card kpi-card--clickable"
          onClick={() => setSummaryKind('Cancelled')}
        >
          <div className="kpi-label">Cancelled</div>
          <div className="kpi-value">{stats.cancelled}</div>
        </button>
        <button
          type="button"
          className="card kpi-card kpi-card--clickable kpi-card--alert"
          onClick={() => setSummaryKind('unpaid')}
        >
          <div className="kpi-label">Unpaid deposit</div>
          <div className="kpi-value">{stats.unpaid}</div>
        </button>
      </div>

      <div className="cit-list-toolbar">
        <div className="cit-list-tabs" role="tablist" aria-label="List view">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'sales-confirmation'}
            className={`cit-list-tabs__btn${view === 'sales-confirmation' ? ' is-active' : ''}`}
            onClick={() => setView('sales-confirmation')}
          >
            By sales confirmation
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'stock-transfer'}
            className={`cit-list-tabs__btn${view === 'stock-transfer' ? ' is-active' : ''}`}
            onClick={() => setView('stock-transfer')}
          >
            By stock transfer
          </button>
        </div>

        <label className="cit-year-filter">
          <span className="cit-year-filter__label">Year</span>
          <select
            className="field-input field-input--compact cit-year-filter__select"
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            aria-label="Filter by year"
          >
            <option value="">All</option>
            {yearOptions.map((year) => (
              <option key={year} value={String(year)}>
                {year}
              </option>
            ))}
          </select>
        </label>

        {view === 'sales-confirmation' ? (
          <label className="cit-missing-filter">
            <input
              type="checkbox"
              checked={missingScOnly}
              onChange={(e) => setMissingScOnly(e.target.checked)}
            />
            No SC file only
            {missingScRows.length ? ` (${missingScRows.length})` : ''}
          </label>
        ) : null}

        <div className="cit-list-toolbar__search page-toolbar__search-wrap">
          <input
            className="field-input page-toolbar__search"
            type="text"
            inputMode="search"
            autoComplete="off"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search"
          />
        </div>

        <div className="cit-page__toolbar-aside">
          <span className="page-count">{countLabel}</span>
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={loading}
            onClick={() => void load()}
          >
            Refresh
          </button>
        </div>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}

      {loading && rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__spinner" />
          <p>Loading records…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p>No Sales Confirmation records yet.</p>
          <Link to={newPath} className="btn-primary btn-sm" style={{ marginTop: '0.75rem' }}>
            Create first entry
          </Link>
        </div>
      ) : view === 'stock-transfer' ? (
        stLineCount === 0 ? (
          <div className="empty-state">
            <p>
              {search.trim()
                ? 'No stock transfers match your search.'
                : 'No stock transfer numbers yet. Save an offset invoice to allocate one.'}
            </p>
            {search.trim() ? (
              <button type="button" className="btn-secondary btn-sm" onClick={() => setSearch('')}>
                Clear search
              </button>
            ) : null}
          </div>
        ) : (
          <CitStList groups={stMonthGroups} detailPath={detailPath} />
        )
      ) : filteredRows.length === 0 ? (
        <div className="empty-state">
          <p>
            {missingScOnly && !search.trim()
              ? 'Every listed sales confirmation already has an SC file.'
              : 'No records match your search.'}
          </p>
          {search.trim() ? (
            <button type="button" className="btn-secondary btn-sm" onClick={() => setSearch('')}>
              Clear search
            </button>
          ) : missingScOnly ? (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => setMissingScOnly(false)}
            >
              Show all records
            </button>
          ) : null}
        </div>
      ) : (
        <div className="cit-sc-list card">
          <div className="cit-sc-list__head" role="row">
            <span>Sales confirmation</span>
            <span>Customer</span>
            <span>Item / SOC qty / Paid qty / Offset qty / Balance qty</span>
            <span>Deposit status</span>
            <span>Document status</span>
            <span aria-hidden />
          </div>
          {filteredRows.map((row) => (
            <CitScListRow key={row.id} row={row} detailPath={detailPath(row.id)} />
          ))}
        </div>
      )}

      <CitSummaryDetailModal
        kind={summaryKind}
        unpaidRows={unpaidDepositDetails}
        statusRows={statusModalRows}
        detailPath={detailPath}
        onClose={() => setSummaryKind(null)}
      />

      <CitBulkAttachImportModal
        open={bulkOpen}
        rows={rows}
        onClose={() => setBulkOpen(false)}
        onImported={() => load({ silent: true })}
      />
    </div>
  );
}
